
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { parseStoredDateTime, formatDateForCalendar } from "@/utils/timeUtils";

export const useBookingEvents = (service: any, currentUserId: string | null) => {
  const [events, setEvents] = useState<any[]>([]);

  const fetchBookings = useCallback(async () => {
    if (!service?.id) return;

    try {
      // Get current date in local format to avoid timezone issues
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayDate = `${year}-${month}-${day}`;
      
      console.log('🔍 Customer view fetching bookings for service:', service.id, 'from date:', todayDate);
      
      // Get bookings for this service - exclude cancelled, rejected, and admin-removed bookings from calendar
      const { data: bookingsData, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('service_id', service.id)
        .or(`booking_date.gte.${todayDate},and(booking_date.is.null,start_time.gte.${todayDate}T00:00:00)`)
        .not('status', 'in', '("declined","cancelled","removed by admin")')
        .order('start_time', { ascending: true });
      
      if (error) throw error;
      
      console.log('🔍 Customer view fetched bookings:', bookingsData?.length || 0, 'bookings');
      console.log('🔍 Blocked bookings in customer view:', bookingsData?.filter(b => b.status === 'blocked') || []);
      
      // Transform bookings into calendar events
      if (bookingsData) {
        // Process each booking into a calendar event
        const transformedEvents = bookingsData.map((booking: any) => {
          // Check if this booking belongs to the current user
          const isUserBooking = currentUserId && booking.customer_id === currentUserId;
          
          // Format time range for the title
          const startTime = parseStoredDateTime(booking.booking_date || booking.start_time, booking.start_time_local);
          const endTime = parseStoredDateTime(booking.booking_date || booking.end_time, booking.end_time_local);
          const timeRange = `${format(startTime, 'h:mm a')} - ${format(endTime, 'h:mm a')}`;
          
          // Customize title based on booking type and who's viewing
          let title = timeRange;
          if (booking.status === 'blocked') {
            title = 'Blocked';
          } else if (booking.status === 'travel') {
            title = 'Travel Time';
          } else if (isUserBooking) {
            title = `My Booking: ${timeRange}`;
          }
          
          // Return the formatted event
          return {
            id: booking.id.toString(),
            title: title,
            start: formatDateForCalendar(startTime),
            end: formatDateForCalendar(endTime),
            classNames: [
              `event-${booking.status}`,
              isUserBooking ? 'event-user-booking' : ''
            ],
            backgroundColor: 'white',
            borderColor: 'transparent',
            textColor: '#333333',
            extendedProps: {
              booking: {
                ...booking,
                is_current_user_booking: isUserBooking
              },
              isUserBooking: isUserBooking
            }
          };
        });
        
        setEvents(transformedEvents);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setEvents([]);
    }
  }, [service, currentUserId]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  return { events, fetchBookings };
};
